import { useEffect, useMemo, useRef } from "react";
import {
  AdvancedMarker,
  ColorScheme,
  InfoWindow,
  Map as GoogleMap,
  useAdvancedMarkerRef,
  useMap,
} from "@vis.gl/react-google-maps";

import { hasCoords, leadKey, PROBATE_COLOR, tierColor } from "./leadUtils";
import { LeadInfoContent } from "./LeadInfoContent";
import type { DealLead } from "./types";

const HOUSTON = { lat: 29.7604, lng: -95.3698 };
const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";

interface DealsMapProps {
  leads: DealLead[]; // filtered auctions + probates
  theme: "light" | "dark";
  activeLeadKey: string | null;
  onMarkerClick: (lead: DealLead) => void;
  onInfoClose: () => void;
  /** Reports the current viewport on every camera change (pan/zoom/fit). */
  onBoundsChanged?: (bounds: google.maps.LatLngBoundsLiteral) => void;
}

/** Fit bounds whenever the identity of the plotted lead set changes. */
const FitBounds = ({ leads }: { leads: DealLead[] }) => {
  const map = useMap();
  const signature = useMemo(() => leads.map(leadKey).sort().join(","), [leads]);
  const lastSignature = useRef<string>("");
  useEffect(() => {
    if (!map || !leads.length || signature === lastSignature.current) return;
    lastSignature.current = signature;
    const bounds = new google.maps.LatLngBounds();
    leads.forEach((l) => bounds.extend({ lat: l.lat!, lng: l.lng! }));
    map.fitBounds(bounds);
  }, [map, leads, signature]);
  return null;
};

const MarkerDot = ({ lead }: { lead: DealLead }) =>
  lead.kind === "auction" ? (
    <div
      className="h-4 w-4 rounded-full border-2 border-white shadow"
      style={{ backgroundColor: tierColor(lead.dealTier), opacity: 0.92 }}
    />
  ) : (
    <div
      className="h-3.5 w-3.5 border-2 border-white shadow"
      style={{ backgroundColor: PROBATE_COLOR, opacity: 0.92 }}
    />
  );

const ActiveMarker = ({
  lead,
  onMarkerClick,
  onInfoClose,
}: {
  lead: DealLead;
  onMarkerClick: (lead: DealLead) => void;
  onInfoClose: () => void;
}) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{ lat: lead.lat!, lng: lead.lng! }}
        title={lead.address ?? ""}
        zIndex={10}
        onClick={() => onMarkerClick(lead)}
      >
        <MarkerDot lead={lead} />
      </AdvancedMarker>
      {marker && (
        <InfoWindow anchor={marker} onClose={onInfoClose}>
          <LeadInfoContent item={lead} />
        </InfoWindow>
      )}
    </>
  );
};

/**
 * The themed Deals map. `colorScheme` is a create-time option, so the map is
 * keyed on the theme; the current viewport is captured continuously and
 * restored across that remount.
 */
export const DealsMap = ({
  leads,
  theme,
  activeLeadKey,
  onMarkerClick,
  onInfoClose,
  onBoundsChanged,
}: DealsMapProps) => {
  const plottable = useMemo(() => leads.filter(hasCoords), [leads]);
  const camera = useRef<{
    center: { lat: number; lng: number };
    zoom: number;
  }>({ center: HOUSTON, zoom: 10 });

  return (
    <GoogleMap
      key={theme}
      mapId={MAP_ID}
      colorScheme={theme === "dark" ? ColorScheme.DARK : ColorScheme.LIGHT}
      defaultCenter={camera.current.center}
      defaultZoom={camera.current.zoom}
      gestureHandling="greedy"
      disableDefaultUI={false}
      streetViewControl={false}
      mapTypeControl={false}
      fullscreenControl={false}
      className="h-full w-full"
      onCameraChanged={(e) => {
        camera.current = { center: e.detail.center, zoom: e.detail.zoom };
        if (e.detail.bounds) onBoundsChanged?.(e.detail.bounds);
      }}
    >
      <FitBounds leads={plottable} />
      {plottable.map((lead) => {
        const key = leadKey(lead);
        if (key === activeLeadKey) {
          return (
            <ActiveMarker
              key={key}
              lead={lead}
              onMarkerClick={onMarkerClick}
              onInfoClose={onInfoClose}
            />
          );
        }
        return (
          <AdvancedMarker
            key={key}
            position={{ lat: lead.lat!, lng: lead.lng! }}
            title={lead.address ?? ""}
            onClick={() => onMarkerClick(lead)}
          >
            <MarkerDot lead={lead} />
          </AdvancedMarker>
        );
      })}
    </GoogleMap>
  );
};
